import * as React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import OrderListCard, {
    OrderListCardLabels
} from '../components/OrderListComponents/OrderListCard';
import { Translate } from '../../../../shared/LanguageService';
import { Order } from '../BookingsCalendarTypes';

type ExternalProps = {
    onSelectedOrder: (selectedOrder: Order) => void;
    orders: Order[];
    onSubmitSplitOrder: (formData: FormData) => Promise<void>;
    onClickHideOrders: (orderIds: number[]) => Promise<void>;
    showHiddenOrders: boolean;
    onUnHideOrders: (orderIds: number[]) => Promise<void>;
};

type OrderListProps = {
    labels: OrderListLabels;
} & ExternalProps;

export type OrderListLabels = OrderListCardLabels & { hideOrder: string; showOrder: string };

const OrderListBody = ({
    orders,
    labels,
    onSelectedOrder,
    onSubmitSplitOrder,
    onClickHideOrders,
    showHiddenOrders,
    onUnHideOrders
}: OrderListProps) => {
    const [activeOrder, setActiveOrder] = React.useState<number>(0);
    let [selectedOrderIndexes, setSelectedOrderIndexes] = React.useState<boolean[]>(
        initSelectedOrderIndexes()
    );
    let [selectedOrders, setSelectedOrders] = React.useState<number[]>([]);
    let [lastClickedIndex, setLastClickedIndex] = React.useState<number>(0);

    function initSelectedOrderIndexes(): boolean[] {
        return orders.filter(onOrderVisibility).map((_) => false);
    }

    React.useEffect(() => {
        clearSelection();
    }, [showHiddenOrders, orders]);

    const getSelectedOrder = (orderId: number) => {
        return orders.find((order) => order.orderId === orderId);
    };

    const handleActiveOrder = (orderId: number) => {
        setActiveOrder(orderId);
        if (orderId === 0) {
            onSelectedOrder(null);
        }

        let selectedOrder = getSelectedOrder(orderId);
        onSelectedOrder(selectedOrder);
    };

    function handleNormalClick(index: number) {
        clearSelection();
        setLastClickedIndex(index);
    }

    function getSelectedOrderIds(selectedOrderIndexes: boolean[]) {
        let newOrdersSelected: number[] = [];
        selectedOrderIndexes.forEach((elem, i) => {
            if (elem) {
                newOrdersSelected.push(orders.filter(onOrderVisibility)[i].orderId);
            }
        });
        return newOrdersSelected;
    }

    function handleShiftClick(index: number) {
        let min = Math.min(index, lastClickedIndex);
        let max = Math.max(index, lastClickedIndex);
        setSelectedOrderIndexes((prev) => {
            let newlySelected = prev.map((_, i) => i >= min && i <= max);

            let newOrdersSelected = getSelectedOrderIds(newlySelected);
            setSelectedOrders(newOrdersSelected);

            return newlySelected;
        });
    }

    function handleCtrlClick(index: number) {
        setSelectedOrderIndexes((prev) => {
            let newlySelected = prev.map((elem, i) => {
                if (i === index) {
                    return !elem;
                }

                return elem;
            });

            let newOrdersSelected = getSelectedOrderIds(newlySelected);
            setSelectedOrders(newOrdersSelected);

            return newlySelected;
        });
        setLastClickedIndex(index);
    }

    function handleClickListItem(
        index: number,
        event: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) {
        if (!event.shiftKey && !event.ctrlKey) {
            handleNormalClick(index);
            return;
        }

        if (event.shiftKey) {
            handleShiftClick(index);
        }

        if (event.ctrlKey) {
            handleCtrlClick(index);
        }
    }

    function clearSelection() {
        setSelectedOrderIndexes(initSelectedOrderIndexes());
        setSelectedOrders([]);
        setLastClickedIndex(0);
    }

    function onOrderVisibility(order: Order): boolean {
        if (showHiddenOrders) {
            return true;
        }

        return !order.isHidden;
    }

    function handleClickHideOrders(orderId: number) {
        return function handler() {
            onClickHideOrders(selectedOrders.length > 0 ? selectedOrders : [orderId]).then(() => {
                clearSelection();
            });
        };
    }

    function handleClickUnHideOrders(orderId: number) {
        return function handler() {
            onUnHideOrders(selectedOrders.length > 0 ? selectedOrders : [orderId]).then(() => {
                clearSelection();
            });
        };
    }

    return (
        <Box
            sx={{
                maxHeight: '66vh',
                overflowY: 'auto'
            }}
        >
            <Grid container>
                {orders
                    ? orders.filter(onOrderVisibility).map((order, index) => (
                          <Grid item xs={12} key={index} aria-label="list-card">
                              <OrderListCard
                                  onClickHideOrders={handleClickHideOrders(order.orderId)}
                                  onClickUnHideOrders={handleClickUnHideOrders(order.orderId)}
                                  onClick={handleClickListItem}
                                  index={index}
                                  isSelected={selectedOrderIndexes[index]}
                                  hasMultipleSelection={selectedOrders.length > 1}
                                  onSubmitSplitOrder={onSubmitSplitOrder}
                                  order={order}
                                  labels={{
                                      ...labels,
                                      showOrders:
                                          selectedOrders.length > 0
                                              ? labels.showOrders
                                              : labels.showOrder,
                                      hideOrders:
                                          selectedOrders.length > 0
                                              ? labels.hideOrders
                                              : labels.hideOrder
                                  }}
                                  activeOrder={activeOrder}
                                  onActiveOrder={handleActiveOrder}
                                  isEven={index % 2 == 0}
                                  isLast={index === orders.length - 1}
                              />
                          </Grid>
                      ))
                    : null}
            </Grid>
        </Box>
    );
};

const OrderListTranslationLayer = (props: ExternalProps) => {
    let labels: OrderListLabels = {
        openOrder: Translate('orders/list/bookings-calendar/open-order'),
        orderLocked: Translate('orders/list/bookings-calendar/order-locked-by'),
        lockOrder: Translate('orders/list/bookings-calendar/lock-order'),
        brokerName: Translate('orders/list/bookings-calendar/broker-name'),
        brokerFirmName: Translate('orders/list/bookings-calendar/broker-firm-name'),
        images: Translate('orders/list/bookings-calendar/images'),
        propertyTitle: (displayName) => Translate(displayName),
        express: Translate('orders/list/bookings-calendar/express'),
        photo: Translate('orders/list/bookings-calendar/photo'),
        message: Translate('orders/list/bookings-calendar/message'),
        preferredDate: Translate('orders/list/bookings-calendar/preferred-date'),
        preferredTimeSlot: Translate('orders/list/bookings-calendar/preferred-date-time-slot'),
        client: Translate('orders/list/bookings-calendar/client'),
        ordered: Translate('orders/list/bookings-calendar/ordered'),
        splitOrder: Translate('orders/list/bookings-calendar/split-order'),
        cloneOrder: Translate('view-order/header/clone-order'),
        goToOrder: Translate('orders/list/bookings-calendar/context-menu/goto-order'),
        description: Translate('orders/list/bookings-calendar/split-order/description'),
        submitButton: Translate('orders/list/bookings-calendar/split-order/submit-button'),
        canNotSplitOnNoProducts: Translate(
            'orders/list/bookings-calendar/split-order/validation/no-products'
        ),
        canNotSplitAllProducts: Translate(
            'orders/list/bookings-calendar/split-order/validation/all-products'
        ),
        mainProductTitle: Translate('orders/list/bookings-calendar/split-order/main-product-title'),
        hideOrder: Translate('orders/list/bookings-calendar/hide-order'),
        hideOrders: Translate('orders/list/bookings-calendar/hide-orders'),
        showOrder: Translate('orders/list/bookings-calendar/show-order'),
        showOrders: Translate('orders/list/bookings-calendar/show-orders'),
        tooltipClickUnHideOrder: Translate('orders/list/bookings-calendar/tooltip/clickUnHideOrder'),
        internalComments: Translate('image-processing/page/comments/header/internal'),
        keyPickupAddress: Translate('orders/create/form/input-label/key-pickup-address')
    };
    return <OrderListBody labels={labels} {...props} />;
};

export default OrderListTranslationLayer;
